import { Grid } from "@mui/material";
import { useFormik } from "formik";
import AsyncSelect from "../../../../../components/AsyncSelect";
import Flex from "../../../../../components/Flex";
import Input from "../../../../../components/Input";
import AddressCommon from "../../../../../components/app/provider/patient/add-new-patient/preferences/AddressCommon";
import StyledFormModal from "../../../../../components/globalModal/StyledModal";
import StyledButtonV2 from "../../../../../components/app/StyledButtonV2";
import { DisclosureHost } from "../../../../../hooks/use-disclosure";
import { useSnackbar } from "../../../../../hooks/useSnackbar";
import { useContactDictionaryCreate } from "../../../../../queries/communication/dictionary/use-dictionary-create";
import { showToastOnApiCall, getXtenantId } from "../../../../../utils/utils";
import {
  CONTACT_INITIAL_VALUES,
  CONTACT_TYPE_OPTIONS,
  CONTACT_VALIDATION_SCHEMA,
} from "./contact-dictionary-constant";
import { useContactDictionaryUpdate } from "../../../../../queries/communication/dictionary/use-dicationary-update";
import { useQueryClient } from "@tanstack/react-query";
import CustomTextArea from "../../../../../components/CustomTextArea";
import NumberInput from "../../../../../components/NumberInput";

interface ContactFormProps {
  disclosureHost: DisclosureHost;
}

const UpsertContactDictionary = ({ disclosureHost }: ContactFormProps) => {
  const prefillData = disclosureHost?.data;
  const showSnackbar = useSnackbar();
  const queryClient = useQueryClient();
  const { mutateAsync: createContactDictionary } = useContactDictionaryCreate();
  const { mutateAsync: updateContactDictionary } = useContactDictionaryUpdate();

  const handleCreateUpdateContactDictionary = async (payload: any) => {
    showToastOnApiCall({
      apiCall: prefillData
        ? updateContactDictionary(payload)
        : createContactDictionary(payload),
      onSave: () => {
        queryClient.invalidateQueries({
          queryKey: ["contact-dictionary-list"],
        });
        disclosureHost.close();
      },
      showSnackbar,
    });
  };

  const formik = useFormik({
    initialValues: prefillData || CONTACT_INITIAL_VALUES,
    validationSchema: CONTACT_VALIDATION_SCHEMA,
    onSubmit: (values) => {
      const payload = {
        contactDictionary: {
          ...values,
          contactType: values?.contactType?.value,
        },
        xTENANTID: getXtenantId(),
      };

      // if (prefillData) {
      handleCreateUpdateContactDictionary(payload);
      // } else {
      //   console.log("values", values);
      // }
    },
    enableReinitialize: true,
  });

  const handleClose = () => {
    formik.resetForm();
    disclosureHost.close();
  };

  return (
    <StyledFormModal
      open={disclosureHost.opened}
      onClose={handleClose}
      dialogTitle={prefillData ? "Edit Contact" : "Add New Contact"}
      dialogAction={
        <Flex gap={1} justifyContent={"flex-end"} width={"100%"}>
          <StyledButtonV2
            label="Cancel"
            variant="outlined"
            onClick={handleClose}
          />
          <StyledButtonV2
            label="Save"
            variant="contained"
            onClick={formik.submitForm}
          />
        </Flex>
      }
      sx={{
        "& .MuiDialog-paper": {
          width: "35vw",
          height: "50vh",
        },
      }}
    >
      <form onSubmit={formik.handleSubmit}>
        <Grid container spacing={2} pt={2}>
          {/* Contact Type */}
          <Grid item xs={6}>
            <AsyncSelect
              label="Contact Type"
              options={CONTACT_TYPE_OPTIONS}
              value={formik.values.contactType}
              onChange={(val) => formik.setFieldValue("contactType", val)}
              placeholder="Select Contact Type"
              required
              error={
                formik.touched.contactType && (formik.errors.contactType as any)
              }
            />
          </Grid>

          {/* first Name */}
          <Grid item xs={6}>
            <Input
              label="First Name"
              required
              textFieldProps={{
                placeholder: "Enter First Name",
                name: "firstName",
                value: formik.values.firstName,
                onChange: formik.handleChange,
                onBlur: formik.handleBlur,
              }}
              error={
                formik.touched.firstName && (formik.errors.firstName as any)
              }
            />
          </Grid>

          {/* Last Name */}
          <Grid item xs={6}>
            <Input
              label="Last Name"
              required
              textFieldProps={{
                placeholder: "Enter Last Name",
                name: "lastName",
                value: formik.values.lastName,
                onChange: formik.handleChange,
                onBlur: formik.handleBlur,
              }}
              error={formik.touched.lastName && (formik.errors.lastName as any)}
            />
          </Grid>

          {/* Contact Number */}
          <Grid item xs={6}>
            <Input
              required
              label="Contact Number"
              textFieldProps={{
                placeholder: "Enter Contact Number",
                name: "contactNumber",
                value: formik.values.contactNumber,
                onChange: formik.handleChange,
                onBlur: formik.handleBlur,
              }}
              error={
                formik.touched.contactNumber &&
                (formik.errors.contactNumber as any)
              }
            />
          </Grid>

          {/* Fax Number */}
          <Grid item xs={6}>
            <NumberInput
              label="Fax Number"
              textFieldProps={{
                placeholder: "Enter Fax Number",
                name: "faxNumber",
                value: formik.values.faxNumber,
                onChange: formik.handleChange,
                onBlur: formik.handleBlur,
              }}
              error={
                formik.touched.faxNumber && (formik.errors.faxNumber as any)
              }
            />
          </Grid>

          {/* Email ID */}
          <Grid item xs={12}>
            <Input
              label="Email ID"
              textFieldProps={{
                placeholder: "Enter Email ID",
                name: "email",
                value: formik.values.email,
                onChange: formik.handleChange,
                onBlur: formik.handleBlur,
                type: "email",
              }}
              error={formik.touched.email && (formik.errors.email as any)}
            />
          </Grid>

          {/* Address Section */}
          <AddressCommon
            formik={
              {
                values: formik.values,
                setFieldValue: formik.setFieldValue,
                handleBlur: formik.handleBlur,
                handleChange: formik.handleChange,
              } as any
            }
            formikKey={`address`}
          />
        </Grid>

        {/* Description */}
        <Grid item xs={12} mt={2}>
          <CustomTextArea
            label="Description"
            id="outlined-multiline-static"
            rows={3}
            variant="outlined"
            name="description"
            placeholder="Enter Description"
            onBlur={formik.handleBlur}
            value={formik.values?.description}
            onChange={formik.handleChange}
          />
        </Grid>
      </form>
    </StyledFormModal>
  );
};

export default UpsertContactDictionary;
